import { Component, OnInit, Inject, ViewChild, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PricebookRow } from './pricebook-row.model';
import { PricebookDataTableService } from './pricebook.service';
import { IColumn, ColumnType } from '../../common/interfaces/IColumn.interface';
import { DataTableFilter, FilterType, RowChanges } from '../../common/models/DataTableFilter.model';
import { TranslationService } from '../../common/services/translation.service';
import { IPagedDataTable } from '../../common/interfaces/IPagedDataTable.interface';
import { TenantSettings } from '../../common/services/tenant-settings.service';
import { RecurringPeriodType } from '../../../product-module/product.model';
import { CpqNumberOptionsBuilder } from '../../directives/validator/cpq-number-options-builder';
import { CpqToastrService } from '../../cpq-toastr/cpq-toastr.service';

@Component({
    selector: 'cpq-setup-cpq-pricebook-lookup-dialog',
    templateUrl: './cpq-pricebook-lookup-dialog.component.html',
    styleUrls: ['./cpq-pricebook-lookup-dialog.component.scss'],
    providers: [PricebookDataTableService],
    standalone: false
})
export class CpqPricebookLookupDialogComponent implements OnInit {
  public columns = new Array<IColumn>();
  public filter: DataTableFilter;

  public parsingIsMandatory: boolean;
  public type = 'av'; // attribute value
  public changedRows = new Array<PricebookRow>();
  public guid: string = 'shared_cpq_pricebook_lookup_dialog_component';

  @ViewChild('pricebookLookupDialog', { static: true }) dialog: ElementRef;

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<CpqPricebookLookupDialogComponent>,
    private translationService: TranslationService,
    public pricebookService: PricebookDataTableService,
    public tenantSettings: TenantSettings,
    private toastr: CpqToastrService) {
    }

  ngOnInit() {
    this.data.lookup.recurringPricePeriods = this.data.lookup.recurringPricePeriods.filter(function(el) { return el.id !== RecurringPeriodType.Formula; });
    this.data.lookup.recurringCostPeriods = this.data.lookup.recurringCostPeriods.filter(function(el) { return el.id !== RecurringPeriodType.Formula; });

    if (this.data.catalogCode && this.data.catalogCode.length > 0 && this.data.catalogCode.includes('<*')) {
      this.parsingIsMandatory = true;
    } else {
      this.filter = new DataTableFilter();
      this.filter.pager.itemsPerPage = 100;
      this.filter.filters.push(
        {
          name: 'partNumber',
          value: this.data.catalogCode
        },
        {
          name: 'priceCode',
          value: this.data.pricingCode
        }
      );

      this.columns = [
        {
          prop: 'name',
          name: this.translationService.translate('Admin.PricebookTableAddEdit.Name'),
          sortable: false,
          visible: true
        },
        {
          prop: 'partNumber',
          name: this.translationService.translate('Admin.PricebookTableAddEdit.PartNumber'),
          sortable: false,
          visible: true
        },
        {
          prop: 'priceCode',
          name: this.translationService.translate('Admin.AddEditPricebookEntry.PriceCode'),
          sortable: false,
          visible: true,
          editable: true,
          required: false
        },
        {
          prop: 'lblCurrencySign',
          name: this.translationService.translate('add_edit_currency.JSCurrencyColumn'),
          sortable: false,
          visible: true
        },
        {
          prop: 'price',
          name: this.translationService.translate('Admin.AddEditPricebookEntry.Price'),
          sortable: false,
          editable: true,
          required: false,
          numberOptions: new CpqNumberOptionsBuilder().allowZero().build(),
          visible: true,
          type: ColumnType.Number
        },
        {
          prop: 'cost',
          name: this.translationService.translate('Admin.AddEditPricebookEntry.Cost'),
          sortable: false,
          editable: true,
          required: false,
          numberOptions: new CpqNumberOptionsBuilder().allowZero().build(),
          visible: true,
          type: ColumnType.Number
        },
        {
          prop: 'recurringPrice',
          name: this.translationService.translate('Admin.AddEditPricebookEntry.RecurringPrice'),
          sortable: false,
          editable: true,
          required: false,
          numberOptions: new CpqNumberOptionsBuilder().allowZero().build(),
          visible: true,
          type: ColumnType.Number
        },
        {
          prop: 'recurringPricePeriod',
          name: this.translationService.translate('Admin.AddEditPricebookEntry.RecurringPricePeriod'),
          sortable: false,
          editable: true,
          required: false,
          visible: true,
          hidden: !this.tenantSettings.useRecurringPrice || !this.tenantSettings.showCustomPricingRecurringPeriodFields,
          type: ColumnType.Dropdown,
          items: this.data.lookup.recurringPricePeriods
        },
        {
          prop: 'recurringCost',
          name: this.translationService.translate('Admin.AddEditPricebookEntry.RecurringCost'),
          sortable: false,
          editable: true,
          required: false,
          numberOptions: new CpqNumberOptionsBuilder().allowZero().build(),
          visible: true,
          type: ColumnType.Number
        },
        {
          prop: 'recurringCostPeriod',
          name: this.translationService.translate('Admin.AddEditPricebookEntry.RecurringCostPeriod'),
          sortable: false,
          editable: true,
          required: false,
          visible: true,
          hidden: !this.tenantSettings.useRecurringPrice || !this.tenantSettings.showCustomPricingRecurringPeriodFields,
          type: ColumnType.Dropdown,
          items: this.data.lookup.recurringCostPeriods
        },
        {
          prop: 'priceDescription',
          name: this.translationService.translate('Admin.AddEditPricebookEntry.Description'),
          sortable: false,
          editable: true,
          required: false,
          visible: true,
          type: ColumnType.Text
        },
      ];

      if(this.tenantSettings.showEffectiveDates4PricebookEntries) {
        this.columns.push(
          {
            prop: 'validFrom',
            name: this.translationService.translate('PriceBookEntries.LabelValidFrom'),
            sortable: false,
            editable: true,
            required: false,
            visible: true,
            type: ColumnType.DatePicker
          },
          {
            prop: 'validUntil',
            name: this.translationService.translate('Admin.Pricebooks.Entries.ValidUntil'),
            sortable: false,
            editable: true,
            required: false,
            visible: true,
            type: ColumnType.DatePicker
          },
        )
      }
    }
  }

  cancel() {
    this.dialogRef.close(null);
  }

  onRowChanged(rowChanges: RowChanges) {
    this.changedRows = rowChanges.modifiedRows;
  }

  onRowsChanged(pagedData: IPagedDataTable<PricebookRow>) {
    this.changedRows = [];
  }

  save() {
    this.pricebookService.save(this.changedRows).subscribe(() => {
      this.toastr.success(this.translationService.translate('Actions.GenericAction.SaveSuccess'));
      this.dialogRef.close();
    });
  }
}
